<template>
  <div class="chat-page">
    <MainTitle
      v-if="!isMobile"
      title="Practice. Learn. Repeat. 💬"
    />
    <div
      class="chat-session-container"
      data-test="chat-session-container"
      :style="containerHeightStyle"
    >
      <Toast />
      <ConfirmDialog />

      <!-- Header with scenario title and end button -->
      <div class="chat-session-header">
        <div class="header-left">
          <Button
            text
            icon="pi pi-arrow-left"
            label="Exit"
            class="exit-btn"
            @click="showExitConfirm"
          />
          <div class="scenario-title-section">
            <span class="scenario-emoji">{{ scenarioEmoji }}</span>
            <span class="scenario-title">{{ scenarioTitle }}</span>
          </div>
        </div>
        <Button
          text
          icon="pi pi-times"
          label="End Chat"
          class="end-chat-btn"
          :loading="isEnding"
          @click="showEndConfirm"
        />
      </div>

      <!-- Chat messages -->
      <div
        ref="messagesContainer"
        class="chat-messages-container"
      >
        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['message', msg.role]"
        >
          <!-- Message bubble with optional correction indicator -->
          <div
            :class="['message-content', { 'has-correction': msg.role === 'user' && msg.corrections, 'has-audio': msg.role === 'ai' && msg.audioUrl }]"
            @click="msg.role === 'user' && msg.corrections && toggleCorrection(index)"
          >
            <!-- Show mic icon while transcribing -->
            <span
              v-if="msg.isTranscribing"
              class="transcribing-indicator"
              title="Transcribing audio…"
            >
              <i class="pi pi-microphone" />
              <span class="transcribing-text">Transcribing…</span>
            </span>
            <!-- Normal message content -->
            <span v-else>{{ msg.content }}</span>
            <!-- Corrections loading spinner -->
            <span
              v-if="msg.role === 'user' && pendingCorrectionsIndex === index && !msg.isTranscribing"
              class="correction-pending"
              title="Analysing your message…"
            >
              <i class="pi pi-spin pi-spinner" />
            </span>
            <!-- Corrections ready badge -->
            <span
              v-else-if="msg.role === 'user' && msg.corrections"
              class="correction-badge"
              :title="expandedCorrections[index] ? 'Hide feedback' : 'View feedback'"
            >✏️</span>
            <!-- Inline audio player for AI messages (inside bubble) -->
            <AudioPlayer
              v-if="msg.role === 'ai' && msg.audioUrl"
              :audio-url="msg.audioUrl"
              :duration-ms="msg.audioDurationMs"
              :message-id="msg.audioMessageId"
              @error="(error) => console.error('[Chat] Audio error:', error)"
            />
          </div>

          <!-- Inline correction panel -->
          <transition name="correction-slide">
            <div
              v-if="msg.role === 'user' && msg.corrections && expandedCorrections[index]"
              class="correction-panel"
            >
              <!-- Header row: title + nativeness score -->
              <div class="correction-panel-header">
                <i class="pi pi-pencil" />
                <span>Feedback</span>
                <span class="correction-score">
                  Nativeness {{ msg.corrections.nativeness_score }}/100
                </span>
              </div>

              <!-- Overall feedback -->
              <p
                v-if="msg.corrections.overall_feedback"
                class="correction-overall"
              >
                {{ msg.corrections.overall_feedback }}
              </p>

              <!-- Clarity indicator -->
              <div class="correction-clarity">
                <i :class="msg.corrections.is_message_clear ? 'pi pi-check-circle clarity-ok' : 'pi pi-times-circle clarity-warn'" />
                <span>{{ msg.corrections.is_message_clear ? 'Message is clear' : 'Message may be unclear' }}</span>
              </div>

              <!-- Per-suggestion rows -->
              <template v-if="msg.corrections.has_suggestions">
                <div
                  v-for="(s, si) in msg.corrections.suggestions"
                  :key="si"
                  class="correction-suggestion"
                >
                  <div class="correction-suggestion-header">
                    <span :class="['correction-severity', 'severity-' + s.severity]">{{ s.severity }}</span>
                    <span class="correction-type">{{ s.type }}</span>
                  </div>
                  <div class="correction-row">
                    <span class="correction-label">Original:</span>
                    <span class="correction-original">{{ s.original }}</span>
                  </div>
                  <div class="correction-row">
                    <span class="correction-label">Suggestion:</span>
                    <span class="correction-improved">{{ s.suggestion }}</span>
                  </div>
                  <p
                    v-if="s.explanation"
                    class="correction-explanation"
                  >
                    {{ s.explanation }}
                  </p>
                </div>
              </template>
            </div>
          </transition>

          <div class="message-timestamp">
            {{ formatTime(msg.timestamp) }}
          </div>
        </div>

        <!-- Typing indicator -->
        <div
          v-if="isAITyping"
          class="message ai"
        >
          <div class="typing-indicator">
            <span /><span /><span />
          </div>
        </div>
      </div>

      <!-- Message input -->
      <div class="chat-input-container">
        <!-- Text Input Row -->
        <div class="input-row">
          <InputText
            v-show="!isRecordingAudio"
            v-model="userMessage"
            placeholder="Type your message..."
            class="message-input"
            :disabled="isLoading || isEnding"
            @keyup.enter="sendMessage"
            @focus="onInputFocus"
            @blur="onInputBlur"
          />
          <AudioRecorder
            v-if="audioSupported"
            :class="['audio-recorder-inline-wrapper', { 'recording-mode': isRecordingAudio }]"
            @audio-recorded="onAudioRecorded"
            @recording-started="isRecordingAudio = true"
            @recording-stopped="isRecordingAudio = false"
            @recording-cancelled="isRecordingAudio = false"
          />
          <Button
            icon="pi pi-send"
            class="send-btn"
            :loading="isLoading"
            :disabled="!userMessage.trim() || isLoading || isEnding || isRecordingAudio"
            @click="sendMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import MainTitle from '@/components/title/MainTitle.vue'
import AudioRecorder from '@/components/chatbot/AudioRecorder.vue'
import AudioPlayer from '@/components/chatbot/AudioPlayer.vue'
import { sidebarWidth } from '@/components/sidebar/state'
import { StorageService } from '@/services/StorageService'
import {
  getChatSessionWebSocket,
  disconnectChatSessionWebSocket
} from '@/services/chatSessionService'
import {
  CHAT_MESSAGE_TYPE_SCENARIO_START,
  CHAT_MESSAGE_TYPE_AI_MESSAGE,
  CHAT_MESSAGE_TYPE_SESSION_END,
  CHAT_MESSAGE_TYPE_ERROR,
  CHAT_MESSAGE_TYPE_CORRECTIONS,
  CHAT_MESSAGE_TYPE_RAG_ANSWER_CHUNK,
  CHAT_MESSAGE_TYPE_USER_MESSAGE_TRANSCRIBED,
  CHAT_MESSAGE_TYPE_TRANSCRIPTION_FAILED,
  CHAT_MESSAGE_TYPE_AI_MESSAGE_AUDIO,
  CHAT_MESSAGE_TYPE_TTS_FAILED,
  CHAT_SESSION_END_CONFIRM_MSG,
  CHAT_SESSION_MIN_MESSAGES,
  CHAT_SESSION_MIN_MESSAGES_WARNING,
  CHAT_SESSION_END_ERROR_MSG,
  FAILED_STATUS
} from '@/assets/constants/constants'

export default {
  name: 'ChatSession',
  components: { Button, InputText, Toast, ConfirmDialog, MainTitle, AudioRecorder, AudioPlayer },
  props: {
    sessionId: {
      type: String,
      required: true
    }
  },
  setup() {
    const confirm = useConfirm()
    return { confirm, sidebarWidth }
  },
  data() {
    return {
      messages: [],
      userMessage: '',
      isLoading: false,
      isEnding: false,
      isAITyping: false,
      userMessageCount: 0,
      ws: null,
      isConnected: false,
      isMobile: window.innerWidth <= 599,
      expandedCorrections: {},
      pendingCorrectionsIndex: null,
      streamingMessageIndex: null,
      pendingTranscriptionIndex: null, // Track message awaiting transcription
      // Audio state
      isRecordingAudio: false,
      // Visual viewport height for mobile keyboard handling
      visualViewportHeight: window.visualViewport ? window.visualViewport.height : window.innerHeight,
      audioMessageIds: new Set() // Track message IDs that have audio
    }
  },
  computed: {
    activeSession() {
      return this.$store.state.chat.activeSession || {}
    },
    subjectId() {
      return this.activeSession.subject_id
    },
    scenarioId() {
      return this.activeSession.scenario_id
    },
    scenarioTitle() {
      return this.activeSession.scenario_title || 'Chat Session'
    },
    scenarioEmoji() {
      return this.activeSession.scenario_emoji || '💬'
    },
    audioSupported() {
      return !!(
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder
      )
    },
    containerHeightStyle() {
      if (!this.isMobile) return {}
      return { height: `${this.visualViewportHeight}px` }
    }
  },
  created() {
    // Guard: redirect if no active session
    console.log('[ChatSession] Component created, activeSession:', this.activeSession)
    if (!this.activeSession.id) {
      console.warn('[ChatSession] No active session found, redirecting to ChatSubjectInput')
      this.$router.replace({ name: 'ChatSubjectInput' })
    } else {
      console.log('[ChatSession] Active session found:', this.activeSession.id)
    }
  },
  async mounted() {
    console.log('[ChatSession] Component mounted, scenarioId:', this.scenarioId)
    await this.initializeWebSocket()
    // Use visualViewport for accurate height when mobile keyboard appears
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onVisualViewportChange)
    }
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeUnmount() {
    this.disconnectWebSocket()
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onVisualViewportChange)
    }
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    /**
     * Auto-scroll to the latest message
     */
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    /**
     * Handle visualViewport resize — fires when mobile keyboard appears/disappears.
     * Updates container height so the chat shrinks above the keyboard.
     */
    onVisualViewportChange() {
      this.visualViewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight
      // Scroll to bottom after layout settles
      setTimeout(() => this.scrollToBottom(), 150)
    },

    /**
     * Handle window resize — fallback for browsers without visualViewport,
     * and keeps isMobile in sync with orientation changes.
     */
    onWindowResize() {
      this.isMobile = window.innerWidth <= 599
      if (!window.visualViewport) {
        this.visualViewportHeight = window.innerHeight
      }
      if (this.isMobile) {
        setTimeout(() => this.scrollToBottom(), 100)
      }
    },

    /**
     * Handle input focus - scroll to bottom when keyboard appears on mobile
     */
    onInputFocus() {
      // Delay ensures keyboard is fully shown before scroll
      setTimeout(() => this.scrollToBottom(), 300)
    },

    /**
     * Handle input blur
     */
    onInputBlur() {
      // Restore height when keyboard is dismissed
      if (window.visualViewport) {
        setTimeout(() => {
          this.visualViewportHeight = window.visualViewport.height
        }, 100)
      }
    },

    /**
     * Initialize WebSocket connection to the chat session
     */
    async initializeWebSocket() {
      let retries = 0
      const maxRetries = 2

      const attempt = async () => {
        try {
          console.log('[ChatSession] Initializing WebSocket for scenario:', this.scenarioId)
          const token = StorageService.getAuthToken()
          
          if (!token) {
            throw new Error('Authentication token not found. Please log in again.')
          }

          this.ws = getChatSessionWebSocket(this.scenarioId, token)
          console.log('[ChatSession] WebSocket instance obtained:', !!this.ws)

          // Register message handler
          this.ws.onMessage(this.handleWebSocketMessage)
          console.log('[ChatSession] Message handler registered')

          // Check if already connected (from connectAndCreateSession)
          if (this.ws.ws && this.ws.ws.readyState === WebSocket.OPEN) {
            console.log('[Chat] WebSocket already connected (readyState: OPEN), skipping connect()')
            this.isConnected = true
          } else {
            console.log('[Chat] WebSocket not connected, calling connect()')
            // Connect to WebSocket if not already connected
            await this.ws.connect()
            this.isConnected = true
            console.log('[Chat] WebSocket connected successfully')
          }

          this.$toast.add({
            severity: 'info',
            summary: 'Connected to chat session',
            life: 2000
          })
          this.scrollToBottom()
          return true // Success
        } catch (error) {
          console.error(`[ChatSession] Connection attempt ${retries + 1}/${maxRetries + 1} failed:`, error)
          
          if (retries < maxRetries) {
            retries++
            console.log(`[ChatSession] Retrying connection in 2 seconds... (${retries}/${maxRetries})`)
            this.$toast.add({
              severity: 'warn',
              summary: 'Reconnecting...',
              detail: `Attempt ${retries + 1}/${maxRetries + 1}`,
              life: 2000
            })
            await new Promise(resolve => setTimeout(resolve, 2000))
            return attempt() // Retry
          } else {
            throw error // Max retries exceeded
          }
        }
      }

      try {
        await attempt()
      } catch (error) {
        console.error('Failed to connect to chat session:', error)
        this.handleError(error, 'WebSocket initialization')
        this.$toast.add({
          severity: FAILED_STATUS,
          summary: 'Connection failed',
          detail: 'Unable to connect to chat. Please refresh the page to try again.',
          sticky: true
        })
      }
    },

    /**
     * Handle incoming WebSocket messages
     */
    handleWebSocketMessage(message) {
      console.log('[Chat] Received message:', message)

      switch (message.type) {
        case CHAT_MESSAGE_TYPE_SCENARIO_START:
          this.onScenarioStart(message)
          break
        case CHAT_MESSAGE_TYPE_AI_MESSAGE:
          this.onAIMessage(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_RAG_ANSWER_CHUNK:
          this.onRAGAnswerChunk(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_CORRECTIONS:
          this.onCorrections(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_USER_MESSAGE_TRANSCRIBED:
          this.onUserMessageTranscribed(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_TRANSCRIPTION_FAILED:
          this.onTranscriptionFailed(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_AI_MESSAGE_AUDIO:
          this.onAIMessageAudio(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_TTS_FAILED:
          this.onTTSFailed(message.data || {})
          break
        case CHAT_MESSAGE_TYPE_SESSION_END:
          this.onSessionEnd(message)
          break
        case CHAT_MESSAGE_TYPE_ERROR:
          this.onChatError(message)
          break
        default:
          console.warn('[Chat] Unknown message type:', message.type)
      }
    },

    /**
     * Handle scenario start message from AI
     */
    onScenarioStart(message) {
      console.log('[Chat] onScenarioStart called with:', message)
      const firstMessage = message.data?.first_message || 'Welcome to the chat!'
      console.log('[Chat] Adding scenario start message:', firstMessage)
      const msgObj = {
        role: 'ai',
        content: firstMessage,
        timestamp: message.timestamp || new Date().toISOString()
      }
      this.messages.push(msgObj)
      console.log('[Chat] Messages count:', this.messages.length, 'Message:', msgObj)
      this.scrollToBottom()
    },

    /**
     * Handle legacy ai_message type (single-shot reply).
     */
    onAIMessage(data) {
      console.log('[Chat] onAIMessage called with:', data)
      this.isAITyping = false

      const msgObj = {
        role: 'ai',
        content: data.answer || '',
        timestamp: data.timestamp || new Date().toISOString()
      }
      this.messages.push(msgObj)
      this.streamingMessageIndex = null
      this.scrollToBottom()
    },

    /**
     * Handle a streaming RAG answer chunk.
     * Expected shape: { chunk: string, is_complete: boolean, timestamp: string }
     * Creates a new AI message bubble on the first chunk, then appends subsequent
     * chunks to it. Finalises when is_complete is true.
     */
    onRAGAnswerChunk(data) {
      console.log('[Chat] onRAGAnswerChunk called with:', data)

      if (this.streamingMessageIndex === null) {
        // First chunk — stop typing indicator and create the message bubble
        this.isAITyping = false

        this.messages.push({
          role: 'ai',
          content: data.answer || data.chunk || '',
          timestamp: data.timestamp || new Date().toISOString()
        })
        this.streamingMessageIndex = this.messages.length - 1
      } else {
        // Subsequent chunk — replace with the latest cumulative answer
        const msg = this.messages[this.streamingMessageIndex]
        this.messages[this.streamingMessageIndex] = { ...msg, content: data.answer || data.chunk || '' }
      }

      if (data.is_complete) {
        this.streamingMessageIndex = null
      }

      this.scrollToBottom()
    },

    /**
     * Handle corrections message (stage 2 — arrives after ai_message).
     * Expected shape: { corrections: {...}, timestamp: string }
     * Attaches the corrections object to the last user message bubble.
     */
    onCorrections(data) {
      console.log('[Chat] onCorrections called with:', data)
      const corrections = data.corrections || null
      if (!corrections) return

      const lastUserIdx = [...this.messages].reverse().findIndex(m => m.role === 'user')
      if (lastUserIdx !== -1) {
        const idx = this.messages.length - 1 - lastUserIdx
        this.messages[idx] = { ...this.messages[idx], corrections }
        console.log('[Chat] Corrections attached to message', idx, corrections)
      }

      // Clear the pending spinner
      this.pendingCorrectionsIndex = null
    },

    /**
     * Toggle the expanded state of an inline correction panel
     */
    toggleCorrection(messageIndex) {
      this.expandedCorrections[messageIndex] = !this.expandedCorrections[messageIndex]
    },

    /**
     * Handle session end from server
     */
    onSessionEnd(message) {
      console.log('[Chat] Session ended by server')
      this.navigateToSummary()
    },

    /**
     * Handle chat error
     */
    onChatError(message) {
      this.$toast.add({
        severity: FAILED_STATUS,
        summary: 'Chat Error',
        detail: message.content || 'An error occurred',
        life: 4000
      })
    },

    /**
     * Handle user message transcription from STT
     */
    onUserMessageTranscribed(data) {
      console.log('[Chat] User message transcribed:', data)
      const text = data.text || ''
      if (!text.trim()) return

      // If there's a pending transcription placeholder, replace it
      if (this.pendingTranscriptionIndex !== null) {
        this.messages[this.pendingTranscriptionIndex] = {
          role: 'user',
          content: text.trim(),
          timestamp: data.timestamp || new Date().toISOString()
        }
        this.pendingCorrectionsIndex = this.pendingTranscriptionIndex
        this.pendingTranscriptionIndex = null
      } else {
        // Fallback: add as new message (shouldn't happen, but just in case)
        const msgObj = {
          role: 'user',
          content: text.trim(),
          timestamp: data.timestamp || new Date().toISOString()
        }
        this.messages.push(msgObj)
        this.pendingCorrectionsIndex = this.messages.length - 1
      }
      this.scrollToBottom()
    },

    /**
     * Handle transcription failure
     */
    onTranscriptionFailed(data) {
      console.log('[Chat] Transcription failed:', data)
      this.$toast.add({
        severity: 'error',
        summary: 'Speech recognition failed',
        detail: data.error || 'Please try again or type your message',
        life: 4000
      })
    },

    /**
     * Handle AI message audio response
     */
    onAIMessageAudio(data) {
      console.log('[Chat] AI message audio received:', data)
      
      try {
        const messageId = data.message_id
        const audioUrl = data.audio_url
        const durationMs = data.duration_ms || 0

        // Validate audio URL
        if (!audioUrl || typeof audioUrl !== 'string') {
          throw new Error('Invalid audio URL received from server')
        }

        // Find the last AI message and attach audio
        let lastAIIndex = -1
        for (let i = this.messages.length - 1; i >= 0; i--) {
          if (this.messages[i].role === 'ai') {
            lastAIIndex = i
            break
          }
        }

        if (lastAIIndex !== -1) {
          this.messages[lastAIIndex] = {
            ...this.messages[lastAIIndex],
            audioUrl: audioUrl,
            audioDurationMs: durationMs,
            audioMessageId: messageId
          }
          this.audioMessageIds.add(messageId)
          console.log('[Chat] Audio attached to message', lastAIIndex)
        } else {
          console.warn('[Chat] No AI message found to attach audio')
        }
      } catch (error) {
        console.error('[Chat] Error processing audio message:', error)
        this.handleError(error, 'Audio message processing')
      }
    },

    /**
     * Handle text-to-speech failure
     */
    onTTSFailed(data) {
      console.log('[Chat] TTS failed:', data)
      
      try {
        const messageId = data.message_id
        
        // Gracefully degrade - don't show error to user as text is already delivered
        console.warn(`[Chat] Audio synthesis failed for message ${messageId}`)
        
        // Only show warning if not too many TTS errors
        const ttsFails = Object.values(this.messages).filter(msg => msg.audioMessageId === messageId && !msg.audioUrl).length
        if (ttsFails < 3) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Audio unavailable',
            detail: 'Text response is still available',
            life: 3000
          })
        }
      } catch (error) {
        console.error('[Chat] Error handling TTS failure:', error)
      }
    },

    /**
     * Handle audio recorded event from AudioRecorder
     */
    async onAudioRecorded(payload) {
      if (!payload || !payload.audioData) {
        this.handleError(new Error('Invalid audio payload'), 'Audio recording')
        return
      }

      const { audioData, durationMs } = payload
      console.log('[Chat] Sending audio message, duration:', durationMs)

      this.isLoading = true
      this.userMessageCount++
      this.streamingMessageIndex = null

      try {
        // Validate connection before sending
        if (!this.isConnected || !this.ws) {
          throw new Error('Not connected to chat session. Please wait and try again.')
        }

        // Add placeholder message with mic icon while transcribing
        const placeholderMsg = {
          role: 'user',
          content: '',
          timestamp: new Date().toISOString(),
          isTranscribing: true
        }
        this.messages.push(placeholderMsg)
        this.pendingTranscriptionIndex = this.messages.length - 1
        this.scrollToBottom()

        // Send audio through WebSocket
        await this.ws.sendUserAudioMessage(audioData, durationMs)
        console.log('[Chat] Audio message sent through WebSocket')

        // Show typing indicator
        this.isAITyping = true
        this.scrollToBottom()

        this.$toast.add({
          severity: 'success',
          summary: 'Audio sent',
          life: 1500
        })
      } catch (error) {
        console.error('Failed to send audio message:', error)
        this.handleError(error, 'Audio transmission')
        // Decrement counter since message wasn't sent
        this.userMessageCount--
        // Remove placeholder on error
        if (this.pendingTranscriptionIndex !== null) {
          this.messages.splice(this.pendingTranscriptionIndex, 1)
          this.pendingTranscriptionIndex = null
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Send user message through WebSocket
     */
    async sendMessage() {
      if (!this.userMessage.trim() || !this.isConnected) {
        console.log('[Chat] Cannot send message. isConnected:', this.isConnected, 'message:', this.userMessage.trim())
        return
      }

      const message = this.userMessage.trim()
      console.log('[Chat] Sending user message:', message)
      this.userMessage = ''
      this.isLoading = true
      this.userMessageCount++
      this.streamingMessageIndex = null

      try {
        // Add user message to chat
        const msgObj = {
          role: 'user',
          content: message,
          timestamp: new Date().toISOString()
        }
        this.messages.push(msgObj)
        const userMessageIndex = this.messages.length - 1
        // Set pending corrections for this user message immediately
        this.pendingCorrectionsIndex = userMessageIndex
        console.log('[Chat] User message added to display, count:', this.userMessageCount, 'Message:', msgObj)

        // Send through WebSocket
        await this.ws.sendUserMessage(message)
        console.log('[Chat] User message sent through WebSocket')

        // Show typing indicator, then scroll so it is visible
        this.isAITyping = true
        this.scrollToBottom()
      } catch (error) {
        console.error('Failed to send message:', error)
        // Decrement counter since message wasn't sent
        this.userMessageCount--
        this.handleError(error, 'Message transmission')
        
        // Offer recovery option
        if (error.message && error.message.includes('WebSocket')) {
          this.$toast.add({
            severity: 'error',
            summary: 'Connection lost',
            detail: 'Attempting to reconnect...',
            life: 3000
          })
          setTimeout(() => this.recoverWebSocketConnection(), 1000)
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Show exit confirmation dialog
     */
    showExitConfirm() {
      this.confirm.require({
        message: CHAT_SESSION_END_CONFIRM_MSG,
        header: 'Leave Chat?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.exitChat(),
        reject: () => {
          // User cancelled, do nothing
        }
      })
    },

    /**
     * Show end chat confirmation dialog
     */
    showEndConfirm() {
      // Check if message count is below minimum
      if (this.userMessageCount < CHAT_SESSION_MIN_MESSAGES) {
        this.confirm.require({
          message: CHAT_SESSION_MIN_MESSAGES_WARNING,
          header: 'End Chat?',
          icon: 'pi pi-exclamation-triangle',
          accept: () => this.endChat(),
          reject: () => {
            // User cancelled, do nothing
          }
        })
      } else {
        this.endChat()
      }
    },

    /**
     * Exit the chat session without ending it
     */
    exitChat() {
      this.disconnectWebSocket()
      this.$router.back()
    },

    /**
     * End the chat session
     */
    async endChat() {
      if (this.isEnding) return

      console.log('[ChatSession] Ending chat session')
      this.isEnding = true
      try {
        // Send session end message through WebSocket
        console.log('[ChatSession] Sending session end message')
        await this.ws.sendSessionEnd()
        console.log('[ChatSession] Session end message sent')

        // Navigate to summary page after a short delay to allow server to process
        setTimeout(() => this.navigateToSummary(), 500)
      } catch (error) {
        console.error('Error ending chat session:', error)
        this.$toast.add({
          severity: FAILED_STATUS,
          summary: CHAT_SESSION_END_ERROR_MSG,
          life: 4000
        })
        this.isEnding = false
      }
    },

    /**
     * Navigate to summary page
     */
    navigateToSummary() {
      console.log('[ChatSession] Navigating to summary, sessionId:', this.sessionId)
      this.disconnectWebSocket()
      this.$router.push({
        name: 'ChatSummary',
        params: { sessionId: this.sessionId, subject: this.subjectId }
      })
    },

    /**
     * Disconnect WebSocket
     */
    disconnectWebSocket() {
      console.log('[ChatSession] Disconnecting WebSocket')
      if (this.ws) {
        this.ws.offMessage(this.handleWebSocketMessage)
        console.log('[ChatSession] Message handler unregistered')
        disconnectChatSessionWebSocket()
        console.log('[ChatSession] WebSocket disconnected')
      }
    },

    /**
     * Check if audio recording is supported
     */
    isAudioSupported() {
      const supported = !!(
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder
      )
      if (!supported) {
        console.warn('[Chat] Audio recording not supported in this browser')
      }
      return supported
    },

    /**
     * Handle unexpected errors with graceful fallback
     */
    handleError(error, context = '') {
      console.error(`[Chat] Error in ${context}:`, error)
      
      // Track error for debugging
      if (window.__chatErrors) {
        window.__chatErrors.push({ timestamp: new Date(), context, error })
      } else {
        window.__chatErrors = [{ timestamp: new Date(), context, error }]
      }

      // Show user-friendly error message
      let message = 'An unexpected error occurred'
      if (error.message) {
        message = error.message
      }

      this.$toast.add({
        severity: 'error',
        summary: `Error in ${context}`,
        detail: message,
        life: 5000
      })
    },

    /**
     * Recover from WebSocket connection errors
     */
    async recoverWebSocketConnection() {
      console.log('[Chat] Attempting to recover WebSocket connection')
      try {
        if (this.ws) {
          this.ws.disconnect()
        }
        await this.initializeWebSocket()
        this.$toast.add({
          severity: 'success',
          summary: 'Reconnected',
          life: 2000
        })
      } catch (error) {
        this.handleError(error, 'WebSocket recovery')
        // Allow user to try again
        this.$toast.add({
          severity: 'error',
          summary: 'Connection failed',
          detail: 'Please refresh the page to continue.',
          sticky: true
        })
      }
    },

    /**
     * Format timestamp for display
     */
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* ─── Page wrapper ───────────────────────────────────────── */
.chat-page {
  padding-left: v-bind(sidebarWidth);
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  max-width: 100vw;
}

/* ─── Chat panel ─────────────────────────────────────────── */
.chat-session-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 45px - 40px);
  margin-top: 70px;
  max-width: 1300px;
  margin-left: auto;
  margin-right: 20px;
  background: linear-gradient(160deg, #fff4ec 0%, #fff8e7 45%, #ffeef2 100%);
  overflow: hidden;
  border: 1px solid rgba(30, 37, 87, 0.08);
  border-radius: 18px;
  margin-bottom: 20px;
}

/* ─── Header ─────────────────────────────────────────────── */
.chat-session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 28px;
  background: #ffffff;
  border-bottom: 1px solid rgba(30, 37, 87, 0.08);
  box-shadow: 0 1px 8px rgba(30, 37, 87, 0.06);
  flex-shrink: 0;
  z-index: 10;
  border-radius: 18px 18px 0 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.exit-btn {
  color: #6b7280 !important;
  font-size: 0.875rem;
}

.exit-btn:hover {
  color: #1e2557 !important;
}

.scenario-title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.scenario-emoji {
  font-size: 1.5rem;
  line-height: 1;
}

.scenario-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e2557;
  letter-spacing: -0.01em;
}

.end-chat-btn {
  color: #dc2626 !important;
  font-size: 0.875rem;
}

/* ─── Messages ───────────────────────────────────────────── */
.chat-messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
}

.chat-messages-container::-webkit-scrollbar {
  width: 4px;
}

.chat-messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages-container::-webkit-scrollbar-thumb {
  background: rgba(30, 37, 87, 0.15);
  border-radius: 2px;
}

.message {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 68%;
}

.message.user {
  align-self: flex-end;
}

.message.ai {
  align-self: flex-start;
}

.message-content {
  padding: 12px 18px;
  border-radius: 20px;
  word-wrap: break-word;
  line-height: 1.6;
  font-size: 0.9375rem;
}

.message.user .message-content {
  background: #1e2557;
  color: #ffffff;
  border-bottom-right-radius: 5px;
  box-shadow: 0 2px 10px rgba(30, 37, 87, 0.22);
}

.message.ai .message-content {
  background: #ffffff;
  color: #1f2937;
  border-bottom-left-radius: 5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07), 0 0 0 1px rgba(0, 0, 0, 0.04);
  position: relative;
}

/* Extra right padding when the audio icon button is present */
.message.ai .message-content.has-audio {
  padding-right: 44px;
}

.message-timestamp {
  font-size: 0.7rem;
  color: #9ca3af;
  padding: 0 4px;
  letter-spacing: 0.02em;
  text-align: left;
}

.message.user .message-timestamp {
  text-align: right;
}

/* ─── Inline correction indicator ───────────────────────── */
.message-content.has-correction {
  cursor: pointer;
  position: relative;
}

.message-content.has-correction:hover {
  opacity: 0.92;
}

.correction-badge {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.8rem;
  vertical-align: middle;
  opacity: 0.85;
  cursor: pointer;
  user-select: none;
}

.correction-pending {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  vertical-align: middle;
  opacity: 0.75;
}

.correction-pending .pi-spinner {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.transcribing-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
}

.transcribing-indicator .pi {
  font-size: 0.9rem;
}

.transcribing-text {
  font-style: italic;
  opacity: 0.8;
}

/* ─── Correction panel ───────────────────────────────────── */
.correction-panel {
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 4px;
  max-width: 100%;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
}

.correction-panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #b45309;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

.correction-panel-header .pi {
  font-size: 0.75rem;
  color: #d97706;
}

.correction-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.correction-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #92400e;
  white-space: nowrap;
  flex-shrink: 0;
}

.correction-original {
  font-size: 0.875rem;
  color: #78350f;
  text-decoration: line-through;
  opacity: 0.7;
}

.correction-improved {
  font-size: 0.875rem;
  color: #1e2557;
  font-weight: 600;
}

.correction-explanation {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 6px 0 0;
  line-height: 1.5;
  border-top: 1px solid rgba(245, 158, 11, 0.2);
  padding-top: 6px;
}

.correction-score {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 700;
  color: #d97706;
  background: #fef3c7;
  border-radius: 20px;
  padding: 2px 8px;
  letter-spacing: 0.04em;
}

.correction-overall {
  font-size: 0.8125rem;
  color: #374151;
  margin: 0 0 10px;
  line-height: 1.5;
  font-style: italic;
}

.correction-clarity {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  margin-bottom: 10px;
  color: #374151;
}

.clarity-ok {
  color: #16a34a;
}

.clarity-warn {
  color: #dc2626;
}

.correction-suggestion {
  border-top: 1px solid rgba(245, 158, 11, 0.25);
  padding-top: 10px;
  margin-top: 6px;
}

.correction-suggestion-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.correction-type {
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: capitalize;
}

.correction-severity {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-radius: 10px;
  padding: 2px 7px;
}

.severity-minor {
  background: #dcfce7;
  color: #15803d;
}

.severity-moderate {
  background: #fef9c3;
  color: #a16207;
}

.severity-major {
  background: #fee2e2;
  color: #b91c1c;
}

/* ─── Correction slide transition ────────────────────────── */
.correction-slide-enter-active,
.correction-slide-leave-active {
  transition: max-height 0.35s ease, opacity 0.25s ease, transform 0.25s ease;
  overflow: hidden;
  max-height: 800px;
}

.correction-slide-enter,
.correction-slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-4px);
}

/* ─── Typing indicator ───────────────────────────────────── */
.typing-indicator {
  display: flex;
  gap: 5px;
  padding: 14px 18px;
  background: #ffffff;
  border-radius: 20px;
  border-bottom-left-radius: 5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07), 0 0 0 1px rgba(0, 0, 0, 0.04);
  width: fit-content;
  align-items: center;
}

.typing-indicator span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9ca3af;
  display: block;
  animation: bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

/* ─── Audio components ───────────────────────────────────── */
.audio-recorder-inline-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* When recording is active, AudioRecorder fills the row (text input is hidden) */
.audio-recorder-inline-wrapper.recording-mode {
  flex: 1;
}

/* ─── Input area ─────────────────────────────────────────── */
.chat-input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 24px;
  background: #ffffff;
  border-top: 1px solid rgba(30, 37, 87, 0.08);
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-input {
  flex: 1;
  border-radius: 24px !important;
  border-color: rgba(30, 37, 87, 0.15) !important;
  padding: 10px 18px !important;
  font-size: 0.9375rem;
  background: #fff9f4;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.message-input:focus {
  border-color: #1e2557 !important;
  box-shadow: 0 0 0 3px rgba(30, 37, 87, 0.1) !important;
  background: #ffffff;
}

.send-btn {
  width: 44px !important;
  height: 44px !important;
  border-radius: 50% !important;
  background: #1e2557 !important;
  border-color: #1e2557 !important;
  flex-shrink: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.send-btn:not(:disabled):hover {
  transform: scale(1.06);
  box-shadow: 0 4px 14px rgba(30, 37, 87, 0.35);
}

/* ─── Responsive ─────────────────────────────────────────── */
@media (max-width: 900px) {
  .chat-session-container {
    max-width: 100%;
  }

  .chat-messages-container {
    padding: 20px 16px;
  }

  .chat-input-container {
    padding: 12px 16px;
  }

  .message {
    max-width: 80%;
  }
}

@media (max-width: 599px) {
  .chat-page {
    padding-left: 0;
    width: 100vw;
    overflow-x: hidden;
  }

  .chat-session-container {
    height: 100dvh;
    margin: 0;
    max-width: 100%;
    width: 100%;
    border: none;
    border-radius: 0;
    box-shadow: none;
    box-sizing: border-box;
  }

  .chat-session-header {
    padding: 10px 14px;
    gap: 8px;
  }

  .scenario-title {
    font-size: 0.875rem;
  }

  .chat-messages-container {
    padding: 14px 12px;
    gap: 12px;
  }

  .chat-input-container {
    padding: 10px 12px;
    box-sizing: border-box;
    width: 100%;
  }

  .input-row {
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
  }

  .message-input {
    min-width: 0;
  }

  .send-btn {
    width: 40px !important;
    height: 40px !important;
    flex-shrink: 0;
  }

  .message {
    max-width: 88%;
  }

  .message-content {
    padding: 10px 14px;
    font-size: 0.875rem;
  }
}
</style>
